<template>
  <div>
    <BaseCard :card-name="tr('Filing Cabinet')" icon="close" :spinnerOn="filingSpinner">
      <div class="file-list">
        <div class="file-header">
          <div class="upload-btn">
            <button class="primary-outlined-button" @click="openUpload" v-show="isAdmin">
              <span class="material-symbols-outlined">cloud_upload</span> Upload File
            </button>
          </div>
          <div class="filter-container">
            <span class="filter-by">Filter By</span>
            <div v-for="(typeValue, typeName) in reportTypes" :key="typeName">
              <div class="file-icon small-icon" :title="typeName" :class="[typeName, { 'icon-off': !typeValue }]"
                @click="toggleFiles(typeName)">
                <span class="material-symbols-outlined"></span>
              </div>
            </div>
          </div>
        </div>
        <div class="base-file-container">
          <ul>
            <li v-for="item in fileItems" :key="item.uuid"
              :class="toggleFullList(item.report_type.short_name) ? 'show' : 'hidden'">
              <div class="file-container">
                <div class="file-icon" :class="item.report_type.short_name" :title="item.report_type.short_name"
                  @click="openFile(item.uuid, item.mimetype)">
                  <span class="material-symbols-outlined"></span>
                </div>
                <div class="file-info" @click="openFile(item.uuid, item.mimetype)">
                  <div class="file-name">{{ item.description }}</div>
                  <div class="file-date">
                    {{ formatDate(item.ordering_date) }}</div>
                </div>
                <div class="file-actions">
                  <button class="material-symbols-outlined icon-button-to-primary"
                    :title="!isAdmin ? 'You do not have permission to perform this action' : 'Edit'"
                    :disabled="!isAdmin" @click="openUpload(item)">edit</button>
                  <button class="material-symbols-outlined icon-button-to-primary"
                    :title="!isAdmin ? 'You do not have permission to perform this action' : 'Delete'"
                    :disabled="!isAdmin" @click="openDelete(item)">delete</button>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </BaseCard>
    <div class="delete-confirmation" v-show="showDelete"></div>
    <Transition name="fade">
      <div class="absolute-panel" v-show="showDelete">
        <DeleteModal cardName="Confirm Deletion" :showDelete="true"
          :actionAndEntity="`delete file '${itemToDelete.description}'`" @close-delete="closeModal('delete')"
          @delete="handleDelete">
        </DeleteModal>
      </div>
    </Transition>
  </div>
</template>

<script setup>
// Copyright (c) 2020-2023 Omics Data Automation, Inc.
// Copyright (c) 2023 datma, inc.
//
// Proprietary and Confidential:
// Unauthorized copying of this file by any medium is strictly prohibited.

import BaseCard from '../BaseCard/BaseCard.vue'
import { fetchWithTimeout } from '@/common/shared'
import { ehrCrudUrl } from '@/settings'
import { computed, defineEmits, defineExpose, onMounted, provide, reactive, ref } from 'vue'
import { useStore } from 'vuex'
import { parseJwt } from '@/common/shared.js'
import { getReportFile } from './getReportFile.js'
import DeleteModal from '@/ux3/components/Common/DeleteModal.vue'
import deleteReportFile from './deleteReportFile'
import getReportList from './getReportList'
import { formatDate } from '@/utils/utils'

const store = useStore()
const tr = (x) => x // inject('tr')

const emit = defineEmits(['close-upload-form', 'open-upload-form', 'refresh-diagnostics-tab'])

const patientId = computed(() => store.getters.session?.patient?.id || '')
const reportTypesBaseUrl = `${ehrCrudUrl}/report-types`

const reportTypesData = ref({})
const fileItems = ref([])
const reportTypes = reactive({})
const filingSpinner = ref(false)
const isAdmin = ref(false)
const filterShowAll = ref(true)
const uuidVal = ref('')
const mimetype = ref('')
const showDelete = ref(false)
const itemToDelete = ref({})

// is already an array we do not need to wrap it in an array like we do elsewhere


const handleDelete = async () => {
  const result = deleteReportFile(itemToDelete.value)
  if (!result) { return }
  const reportList = await getReportListWithTimeout()
  fileItems.value = reportList || []
  // send event to refresh associated diagnostics tab
  emit('refresh-diagnostics-tab', [itemToDelete.value.report_type.short_name])
  updateList()
  closeModal('delete')
}

const openUpload = (item) => {
  emit('open-upload-form', item.uuid ? item : {})
}

const openDelete = (item) => {
  itemToDelete.value = item
  showDelete.value = true
}

const closeModal = async (modalType) => {
  if (modalType === 'upload') {
    emit('close-upload-form')
  } else {
    showDelete.value = false
    //wait for the CSS transistion so we don't see 'undefined'
    setTimeout(() => {
      itemToDelete.value = {}
    }, 1000)
  }
}

const updateList = async () => {
  const result = await getReportListWithTimeout()
  fileItems.value = result || []
}

const getReportListWithTimeout = async () => {
  if (!patientId.value) { return [] }
  filingSpinner.value = true
  const result = await getReportList(patientId)
  filingSpinner.value = false
  return result
}

const getReportTypes = async (reportTypesBaseUrl) => {
  try {
    const fetchParams = {
      method: 'GET',
      headers: {
        'Authorization': 'Bearer ' + store.getters.jwt,
      },
    }
    const url = `${reportTypesBaseUrl}`
    const response = await fetchWithTimeout(url, fetchParams)
    if (!response.ok) {
      console.log(`error: status=${response.status}`)
      return []
    }
    const result = await response.json()
    if (!result) { return [] }
    // Translate the response to the format expected by the frontend.
    const tempValues = result.types
    const returnValues = []
    for (let i = 0; i < result.types.length; i++) {
      if (!tempValues[i].voided) {
        returnValues[tempValues[i].short_name] = false
      }
    }

    return returnValues
  } catch (e) {
    if (e.name === 'AbortError') {
      console.log('timeout error')
    } else {
      console.log('ERROR:', e)
    }
  }
  return []
}

const toggleFiles = (x) => {
  reportTypes[x] = !reportTypes[x]
  const anyPropTrue = Object.keys(reportTypes).some((k) => reportTypes[k])
  anyPropTrue ? filterShowAll.value = false : filterShowAll.value = true
}

const toggleFullList = (x) => {
  return filterShowAll.value || reportTypes[x]
}

const openFile = async (uuid, mime) => {
  store.dispatch('showViewer', true)
  uuidVal.value = uuid
  mimetype.value = mime
  store.dispatch('reportDocType', mime)
  store.dispatch('reportDocument', await getReportFile(uuidVal.value, mimetype.value))
}

onMounted(async () => {
  reportTypesData.value = await getReportTypes(reportTypesBaseUrl)
  for (const key in reportTypesData.value) {
    reportTypes[key] = false
  }
  const result = await getReportListWithTimeout()
  fileItems.value = result || []

  const tokenVal = parseJwt(store.getters.jwt)
  isAdmin.value = tokenVal.realm_access.roles.some((item) => item === 'mypatient-admin')
})

defineExpose({
  updateList,
})

provide('ReportTypes', reportTypesData.value)
</script>

<style lang="scss" scoped>
ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.file-container {
  width: 100%;
  padding: 10px;
  border-bottom: 1px solid var(--appScrollBarBackgroundColor);
  display: flex;
  overflow: hidden;
  flex-direction: row;
  justify-content: space-between;
  transition: all linear 300ms;
}

.hidden {
  display: none;
}

.show {
  display: flex;
}

.filter-container {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 1rem;
  margin-right: 20px;
  margin-top: 1rem;

  select {
    margin-left: 10px;
    padding: 1px;
  }

  span {
    margin-left: 10px;
  }
}

.filter-by {
  font-size: 0.8rem;
  color: var(--Grey600);
  transition: all linear 300ms;
}

.hidden {
  display: none;
}

.show {
  display: flex;
}

.filter-container {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 1rem;
  margin-right: 20px;
  margin-top: 1rem;

  select {
    margin-left: 10px;
    padding: 1px;
  }

  span {
    margin-left: 10px;
  }
}

.filter-by {
  font-size: 0.8rem;
  color: var(--Grey600);
}

.file-icon {
  width: 28px;
  height: 29px;
  margin-right: 10px;
  border-radius: 5px;
  display: flex;
  align-self: center;
}

.file-info {
  flex-grow: 2;
}

.file-download {
  display: flex;
  align-items: center;
  margin-right: 10px;
}

.RADIOLOGY {
  // dynamic class name sent from backend
  background-color: var(--plotColor01);
  color: var(--White);

  ::before {
    font-family: 'Material Symbols Outlined';
    content: '\e125'; // x-ray image
  }
}

.GENOMICS {
  // dynamic class name sent from backend
  background-color: var(--plotColor02);
  color: var(--White);

  ::before {
    font-family: 'Material Symbols Outlined';
    content: '\e0f3'; // DNA imge
  }
}

.PATHOLOGY {
  // dynamic class name sent from backend
  background-color: var(--plotColor03);
  color: var(--White);

  ::before {
    font-family: 'Material Symbols Outlined';
    content: '\ea3a'; // microscope image
  }
}

.CLINICAL {
  // dynamic class name sent from backend
  background-color: var(--plotColor04);
  color: var(--White);

  ::before {
    font-family: 'Material Symbols Outlined';
    content: '\e09e'; // clinical notes
  }
}

.icon-off {
  background-color: var(--Grey400);
  color: var(--White);
}

.small-icon {
  width: 22px;
  height: 23px;
  margin-left: 10px;
  margin-right: 0;
  cursor: pointer;

  span {
    font-size: 18px;
    content: '\e09e'; // clinical notes
  }
}

.icon-off {
  background-color: var(--Grey400);
  color: var(--White);
}

.small-icon {
  width: 22px;
  height: 23px;
  margin-left: 10px;
  margin-right: 0;
  cursor: pointer;

  span {
    font-size: 18px;
  }
}

.file-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin-right: 10px;
}

.file-name {
  max-width: 300px;
  font-weight: 500;
  font-size: 0.8rem;
  text-transform: capitalize;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  text-align: left;
}

.file-icon>span {
  margin: 2px;
}

.file-download>span {
  color: var(--appScrollBarColor);
  transition: all linear 200ms;
  cursor: pointer;
  padding: 2px;
  border-radius: 4px;

  &:hover {
    background-color: var(--appScrollBarColor) !important;
    color: var(--buttonTextColor) !important;
  }
}

.file-date {
  font-weight: 500;
  font-size: 0.8rem;
  color: var(--Grey600);
  display: flex;
  align-self: flex-start;
}

.base-file-container {
  overflow-y: auto;
  margin-bottom: 30px;
  transition: opacity linear 1000ms;
  flex: 1;
}

.base-file-container ul li:hover {
  background-color: var(--Grey100);
  cursor: pointer;
}

.file-upload,
.file-list {
  display: flex;
  flex-direction: column;
  height: 75vh;
  width: 90%;
  margin: auto;
  text-align: left;

  p {
    font-size: 0.9rem;
    color: var(--Grey800);
  }
}

.edit-file {
  display: none;
}

.report-viewer {
  position: fixed;
  z-index: 100;
  top: 70px;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.85);
  overflow-y: auto;

  div {
    width: 70vw;
    margin: auto;
  }
}

.report-viewer .close {
  position: fixed;
  top: 90px;
  right: 40px;
  color: var(--White)
}

.plain-text {
  background-color: var(--White);
  width: 60%;
  margin: auto;
  padding: 40px;
  padding-bottom: 100px;
  text-align: left;
  white-space: pre-line;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 750ms ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade2-enter-active,
.fade2-leave-active {
  transition: opacity 750ms ease;
}

.fade2-enter-from,
.fade2-leave-to {
  opacity: 0;
}

.close {
  cursor: pointer;
}

.delete-confirmation {
  width: 30%;
  margin: auto;
  height: 300px;
  background-color: var(--White);
  border-radius: 16px;
}

.edit-file {
  display: none;
}

.file-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.upload-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;

  .primary-outlined-button {
    display: flex;
    align-items: center;
    margin-left: 10px;

    span {
      padding-right: 8px;
      font-size: 20px;
    }
  }

}

.slide-enter-active {
  -moz-transition-duration: 0.3s;
  -webkit-transition-duration: 0.3s;
  -o-transition-duration: 0.3s;
  transition-duration: 0.3s;
  -moz-transition-timing-function: ease-in;
  -webkit-transition-timing-function: ease-in;
  -o-transition-timing-function: ease-in;
  transition-timing-function: ease-in;
}

.slide-leave-active {
  -moz-transition-duration: 0.3s;
  -webkit-transition-duration: 0.3s;
  -o-transition-duration: 0.3s;
  transition-duration: 0.3s;
  -moz-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
  -webkit-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
  -o-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
  transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
}

.slide-enter-to,
.slide-leave {
  max-height: 100px;
  overflow: hidden;
}

.slide-enter,
.slide-leave-to {
  overflow: hidden;
  max-height: 0;
}

.absolute-panel {
  position: absolute;
  top: 65px;
  left: calc(50% - 23vw);
}
</style>
